import { useState, useCallback } from 'react';
import * as cookie from 'js-cookie';
import AGENTS from './agents';

const COOKIE_KEY = 'talisis_ia_recent_agents';
const MAX_RECENT = 5;
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Reads the recent agents list from cookie.
 * Returns an array of { id, lastUsed } sorted by most recent first.
 */
function getRecentFromCookie() {
    try {
        const raw = cookie.get(COOKIE_KEY);
        if (!raw) return [];
        return JSON.parse(raw);
    } catch {
        return [];
    }
}

/**
 * Saves the recent agents list to cookie.
 */
function saveToCookie(recents) {
    cookie.set(COOKIE_KEY, JSON.stringify(recents), { expires: COOKIE_EXPIRY_DAYS });
}

/**
 * Hook to manage recent IA agents.
 *
 * Returns:
 * - recentAgents: array of agent objects enriched with `lastUsed` timestamp
 * - trackAgent(agentId): call this when the user enters an agent
 */
export default function useRecentAgents() {
    const [recents, setRecents] = useState(getRecentFromCookie);

    const trackAgent = useCallback((agentId) => {
        const current = getRecentFromCookie();
        const filtered = current.filter((item) => item.id !== agentId);
        const updated = [{ id: agentId, lastUsed: Date.now() }, ...filtered].slice(0, MAX_RECENT);
        saveToCookie(updated);
        setRecents(updated);
    }, []);

    // Enrich with full agent data from registry
    const recentAgents = recents
        .map((item) => {
            const agent = AGENTS.find((a) => a.id === item.id);
            if (!agent) return null;
            return { ...agent, lastUsed: item.lastUsed };
        })
        .filter(Boolean);

    return { recentAgents, trackAgent };
}
